import round from 'lodash/round';

import * as commissionModes from 'components/lease_structure/form/commission_modes';
import { UNIT_DOLLARS, UNIT_PERCENT } from 'bundles/backoffice/vouchers/helpers/transaction_side_details';

export function getGrossCommissionAmount(percent, transactionValue) {
  return round(percent * transactionValue / 100.0, 2);
}

export function getGrossCommissionPercent(amount, transactionValue) {
  return transactionValue === 0 ? 0 : round(amount / transactionValue * 100.0, 2);
}

export function recalculateCommissionRate(deal, transactionSide = null) {
  const transactionSideDetails = transactionSide &&
    deal.transactionSideDetails.find(side => side.transactionSide === transactionSide);
  const commissionRate = transactionSide
    ? transactionSideDetails.grossRate
    : deal.commissionRate;
  const grossAmount = transactionSide
    ? transactionSideDetails.grossAmount
    : deal.grossCommissionAmount;

  if (grossAmount == null || !deal.spaceSize || !isPerSpaceSizeUnit(deal.commissionMode)) {
    return commissionRate;
  }

  const requiresLeaseTerm = isPerMonth(deal.commissionMode) || isPerYear(deal.commissionMode);
  if (requiresLeaseTerm && !deal.leaseTerm) {
    return commissionRate;
  }

  const [leaseTermMonths, leaseTermYears] = getLeaseTermMonthsAndYears(deal);
  let leaseTermDivisor = 1;
  if (isPerYear(deal.commissionMode)) {
    leaseTermDivisor = leaseTermYears;
  } else if (isPerMonth(deal.commissionMode)) {
    leaseTermDivisor = leaseTermMonths;
  }

  return round(grossAmount / leaseTermDivisor / deal.spaceSize, 2);
}

export function recalculateLeaseRate(deal) {
  if (deal.transactionValue == null || !deal.leaseTerm) {
    return deal.leaseRate;
  }

  if (isPerSpaceSizeUnit(deal.leaseRateUnits) && !deal.spaceSize) {
    return deal.leaseRate;
  }

  const [leaseTermMonths, leaseTermYears] = getLeaseTermMonthsAndYears(deal);
  const leaseTermDivisor = isPerYear(deal.leaseRateUnits) ? leaseTermYears : leaseTermMonths;
  const spaceSizeDivisor = isPerSpaceSizeUnit(deal.leaseRateUnits) ? deal.spaceSize : 1;
  return round(deal.transactionValue / leaseTermDivisor / spaceSizeDivisor, 2);
}

export function recalculateTransactionValue(deal) {
  if (!canRecalculateTransactionValue(deal)) {
    return deal.transactionValue;
  }

  const [leaseTermMonths, leaseTermYears] = getLeaseTermMonthsAndYears(deal);
  const leaseTermMultiplier = isPerYear(deal.leaseRateUnits) ? leaseTermYears : leaseTermMonths;
  const spaceSizeMultiplier = isPerSpaceSizeUnit(deal.leaseRateUnits) ? deal.spaceSize : 1;

  return deal.leaseRate * leaseTermMultiplier * spaceSizeMultiplier;
}

function canRecalculateTransactionValue(deal) {
  if (!deal.leaseRate || !deal.leaseTerm) {
    return false;
  }

  if (isPerSpaceSizeUnit(deal.leaseRateUnits) && !deal.spaceSize) {
    return false;
  }

  return true;
}

export function recalculateGrossCommissionValues(deal) {
  if (deal.commissionMode === commissionModes.FLAT_RATE) {
    return recalculateFlatRateCommissionValues(deal);
  } else if (isPerSpaceSizeUnit(deal.commissionMode)) {
    return recalculateSpaceSizeUnitBasedCommissionValues(deal);
  } else {
    return recalculateTransactionValueBasedCommissionValues(deal);
  }
}

function recalculateFlatRateCommissionValues(deal) {
  const values = {
    grossCommissionUnit: 'gross_commission_unit_dollars',
  };
  if (deal.hasTransactionSides) {
    values.transactionSideDetails = deal.transactionSideDetails.map(side => ({
      ...side,
      grossPercent: getGrossCommissionPercent(side.grossAmount, deal.transactionValue),
      grossUnit: UNIT_DOLLARS,
    }));
    values.grossCommissionAmount = values.transactionSideDetails.reduce((sum, side) => sum + side.grossAmount, 0);
    values.grossCommissionPercent = values.transactionSideDetails.reduce((sum, side) => sum + side.grossPercent, 0);
  } else {
    values.grossCommissionPercent = getGrossCommissionPercent(deal.grossCommissionAmount, deal.transactionValue);
  }
  return values;
}

function recalculateSpaceSizeUnitBasedCommissionValues(deal) {
  const values = {};
  const commissionRate = deal.hasTransactionSides
    ? deal.transactionSideDetails?.reduce((sum, side) => sum + side.grossRate, 0)
    : deal.commissionRate;

  if (!commissionRate || !deal.spaceSize) {
    return values;
  }

  const requiresLeaseTerm = isPerMonth(deal.commissionMode) || isPerYear(deal.commissionMode);
  if (requiresLeaseTerm && (!deal.leaseTerm || !deal.leaseTermUnits)) {
    return values;
  }

  values.grossCommissionUnit = 'gross_commission_unit_dollars';

  const [leaseTermMonths, leaseTermYears] = getLeaseTermMonthsAndYears(deal);
  let leaseTermMultiplier = 1;
  if (isPerMonth(deal.commissionMode)) {
    leaseTermMultiplier = leaseTermMonths;
  } else if (isPerYear(deal.commissionMode)) {
    leaseTermMultiplier = leaseTermYears;
  }

  if (deal.hasTransactionSides) {
    values.transactionSideDetails = deal.transactionSideDetails.map((side) => {
      const grossAmount = deal.spaceSize * side.grossRate * leaseTermMultiplier;
      return ({
        ...side,
        grossAmount,
        grossPercent: getGrossCommissionPercent(grossAmount, deal.transactionValue),
        grossUnit: UNIT_DOLLARS,
      });
    });
    values.grossCommissionAmount = values.transactionSideDetails.reduce((sum, side) => sum + side.grossAmount, 0);
    values.grossCommissionPercent = values.transactionSideDetails.reduce((sum, side) => sum + side.grossPercent, 0);
    values.commissionRate = commissionRate;
  } else {
    values.grossCommissionAmount = deal.spaceSize * deal.commissionRate * leaseTermMultiplier;
    values.grossCommissionPercent = getGrossCommissionPercent(values.grossCommissionAmount, deal.transactionValue);
  }

  return values;
}

function recalculateTransactionValueBasedCommissionValues(deal) {
  const values = {};

  if (deal.transactionValue != null) {
    if (deal.hasTransactionSides) {
      values.transactionSideDetails = deal.transactionSideDetails.map((side) => {
        const updatedSide = { ...side };
        if (side.grossUnit === UNIT_DOLLARS && side.grossAmount != null) {
          updatedSide.grossPercent = getGrossCommissionPercent(side.grossAmount, deal.transactionValue);
        } else if (side.grossUnit === UNIT_PERCENT && side.grossPercent != null) {
          updatedSide.grossAmount = getGrossCommissionAmount(side.grossPercent, deal.transactionValue);
        }
        return updatedSide;
      });
      // Need to roll up the transaction side values to keep deal level attributes in sync
      values.grossCommissionAmount = values.transactionSideDetails.reduce((sum, side) => sum + side.grossAmount, 0);
      values.grossCommissionPercent = values.transactionSideDetails.reduce((sum, side) => sum + side.grossPercent, 0);
      values.grossCommissionUnit = 'gross_commission_unit_dollars';
    } else {
      if (deal.grossCommissionUnit === 'gross_commission_unit_dollars' && deal.grossCommissionAmount != null) {
        values.grossCommissionPercent = getGrossCommissionPercent(deal.grossCommissionAmount, deal.transactionValue);
      } else if (deal.grossCommissionUnit === 'gross_commission_unit_percent' && deal.grossCommissionPercent != null) {
        values.grossCommissionAmount = getGrossCommissionAmount(deal.grossCommissionPercent, deal.transactionValue);
      }
    }
  }

  return values;
}

function getLeaseTermMonthsAndYears(deal) {
  let leaseTermMonths, leaseTermYears;
  if (deal.leaseTermUnits === 'years') {
    leaseTermMonths = deal.leaseTerm * 12;
    leaseTermYears = deal.leaseTerm;
  } else {
    leaseTermMonths = deal.leaseTerm;
    leaseTermYears = deal.leaseTerm / 12;
  }

  return [leaseTermMonths, leaseTermYears];
}

function isPerSpaceSizeUnit(units) {
  return units && /per_space_size_unit/.test(units);
}

function isPerMonth(units) {
  return units && /month/.test(units);
}

function isPerYear(units) {
  return units && /year/.test(units);
}
